<template>
  <div
    class="session-box"
    :class="sessionState(classroom) && 'session-state-' + sessionState(classroom)"
  >
    <div class="d-flex flex-column flex-md-row">
      <div class="d-flex flex-column">
        <div class="d-flex align-items-center session-box-first-line" :class="{ 'no-favourite': !canBeFavorite }">
          <span
            v-if="canBeFavorite"
            class="icon-favourite"
            :class="{ 'is-favourite': classroom.enrollment && classroom.enrollment.favorite }"
            @click.stop="toggleFavorite(classroom)"
          >
            <i class="isax isax-star"/>
            <i class="isax isax-star-15"/>
          </span>
          <i class="isax isax-calendar-1"/>
          <span class="session-start-date flex-grow-1">
            <span>{{ classroom.start | moment("D MMM YYYY") }}</span>
            <span>{{ classroom.start | moment("HH:mma") }}</span>
          </span>
        </div>
        <div v-if="classroom.main_recording" class="session-box-info-lines mb-1">
          <div class="recording-title">
            <span>{{ classroom.main_recording.name }}</span>
          </div>
        </div>
        <div class="d-flex flex-column flex-md-row align-items-md-center session-box-info-lines mb-1">
          <div class="session-duration">
            <i class="isax isax-timer-1"/>
            <span>{{ $t("recordings.duration") }}: {{ classDuration(classroom) }} {{ $t("sessions.min") }}</span>
          </div>
          <div class="session-host" v-if="hostName">
            <i class="isax isax-profile-circle"/>
            <span>{{ $t("sessions.host") }}: {{ hostName }}</span>
          </div>
        </div>
        <div v-if="classroom.description && classroom.description.trim()" class="session-box-info-lines mb-1">
          <span>{{ $t("sessions.notes") }}</span>
          <span>{{ classroom.description }}</span>
        </div>
      </div>
      <div class="d-flex ml-md-auto align-items-start session-box-right-block">
        <template v-if="sessionState(classroom) === 'not-started'">
          <div class="d-flex align-items-center">
            <div class="sessions-banner mr-1">
              <span>{{ $t("sessions.notstarted") }}</span>
            </div>
          </div>
        </template>
        <template v-else-if="sessionState(classroom) === 'in-progress'">
          <div class="d-flex align-items-center">
            <div class="sessions-banner-in-progress mr-1">
              <span class="pulse-dot"></span>
              <span>{{ $t("sessions.inprogress") }}</span>
            </div>
          </div>
        </template>
        <div v-if="classroom.main_recording" class="checkbox-wrap list">
          <input :id="'viewed-'+classroom.main_recording.id" v-model="classroom.main_recording.viewed" @change="toggleRecordingView(classroom.main_recording)" type="checkbox" autocomplete="off">
          <label :for="'viewed-'+classroom.main_recording.id">{{ $t("sessions.markrecordingviewed") }}</label>
        </div>
        <b-dropdown v-if="user_roles.indexOf('ROLE_ADMIN') !== -1 || user_roles.indexOf('ROLE_PRESENTER') !== -1"
                    right menu-class="button-list-icons small-icons"
                    variant=" icon-button session-row-options list-item-icon has-tooltip d-flex align-items-center"
                    toggle-class="text-decoration-none" no-caret>
          <template :id="'session-options-' + classroom.id" #button-content>
            <dots-horizontal-icon :size="24"/>
            <b-tooltip :target="'session-options-' + classroom.id" triggers="hover">
              {{ $t("sessions.options") }}
            </b-tooltip>
          </template>
          <b-dropdown-item-btn v-if="user_roles.indexOf('ROLE_ADMIN') != -1" link-class="button" v-b-toggle.sidebar-no-header="'sidebar-edit-' + classroom.id">
            <span>{{ $t("sessions.editconfig") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn @click="dispatchShowReportEvent(classroom.id)" link-class="button" v-b-toggle.sidebar-no-header="'sidebar-report-' + classroom.id">
            <span>{{ $t("sessions.seelivereport") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn v-if="hasRecordingReport" @click="dispatchShowRecordingReportEvent(classroom.main_recording.id)" link-class="button" v-b-toggle.sidebar-no-header="'sidebar-event-report-' + classroom.main_recording.id">
            <span>{{ $t("sessions.seerecordingreport") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn v-if="user_roles.indexOf('ROLE_ADMIN') != -1 && !classroom.fromGcc" link-class="button" @click="$emit('confirm-delete', classroom)">
            <span>{{ $t("sessions.deletesession") }}</span>
          </b-dropdown-item-btn>
        </b-dropdown>
      </div>
    </div>
    <div class="session-controls">
      <div class="session-attendance align-self-start align-self-md-center" v-if="hasEnded(classroom) && supportsAttendance">
        <div class="sessions-banner sessions-ok-banner mr-1" v-if="classroom && classroom.enrollment && classroom.enrollment.attendanceQualified">
          <i class="isax isax-tick-circle4"></i>
          <span>{{ $t("sessions.attendedlive") }}</span>
          <i class="isax isax-info-circle ml-2" :id="'attendance-info-' + classroom.id"></i>
          <b-tooltip :target="'attendance-info-' + classroom.id" triggers="hover" placement="bottom" custom-class="attendance-info-tooltip">
            {{ $t("sessions.attendedlivetooltip") }}
          </b-tooltip>
        </div>
        <div class="sessions-banner" v-if="classroom && classroom.enrollment && classroom.enrollment.attendanceQualified === false">
          <span>{{ $t("sessions.notattendedlive") }}</span>
          <i class="isax isax-info-circle ml-2" :id="'attendance-info-' + classroom.id"></i>
          <b-tooltip :target="'attendance-info-' + classroom.id" triggers="hover" placement="bottom" custom-class="attendance-info-tooltip">
            {{ $t("sessions.notattendedlivetooltip") }}
          </b-tooltip>
        </div>
      </div>
      <div class="d-flex flex-column flex-md-row align-items-center ml-md-auto session-controls-buttons">
        <div v-if="classroom.main_recording && classroom.main_recording.playing && !classroom.main_recording.viewed" class="sessions-banner sessions-orange-banner mr-1">
          <i class="isax isax-clock mr-1"></i>
          <span v-html="$t('sessions.recordingremainingminutes', { min: remainingMinutes(classroom.main_recording) })"></span>
        </div>
        <button v-if="(user_roles.indexOf('ROLE_ADMIN') !== -1 || user_roles.indexOf('ROLE_PRESENTER') !== -1) && profiles.indexOf('argos') !== -1 && isOngoingBeforeX(classroom, 30)"
                class="page-controls__primary-action button secondary button-panic mr-1" @click="$emit('show-incident', classroom)" :title="$t('sessions.panicbutton.title')">
          <phone-alert-outline :size="20"/>
          <span>{{ $t("sessions.panicbutton.text") }}</span>
        </button>
        <button class="page-controls__primary-action button primary short" v-if="!hasEnded(classroom)" v-show="classroom && classroom.enrollment && classroom.enrollment.launchUrl" @click="launchUrl(classroom.enrollment.launchUrl, '_blank')">
          <span>{{ $t("sessions.joinclass") }}</span>
        </button>
        <button class="page-controls__primary-action button primary short" v-if="classroom.main_recording" @click="launchClickUrl('_blank', classroom.main_recording)">
          <i class="isax isax-play" v-if="!classroom.main_recording.loadingLaunchUrl || classroom.main_recording.launchUrl"></i>
          <loading-icon class="svg-icon small default rotate" :size="24" v-if="classroom.main_recording.loadingLaunchUrl"/>
          <span>{{ $t("sessions.viewrecording") }}</span>
        </button>
        <b-dropdown v-if="classroom.main_recording && classroom.main_recording.canDownload"
                    right menu-class="button-list-icons small-icons"
                    variant=" button secondary mb-0 ml-2 d-flex align-items-center"
                    toggle-class="text-decoration-none">
          <template :id="'download-options-' + classroom.id" #button-content>
            <i class="isax isax-import-1"></i>
            <span>{{ $t("recordings.downloads") }}</span>
          </template>
          <b-dropdown-item-btn @click="getDownloadLinkClick(classroom.main_recording)" link-class="button">
            <span>{{ $t("recordings.downloadsession") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn v-if="classroom.main_recording.captionsUrl" @click="launchUrl(classroom.main_recording.captionsUrl, '_self')" link-class="button">
            <span>{{ $t("recordings.downloadtranscription") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn v-if="classroom.main_recording.summary" @click="downloadSummary(classroom.main_recording)" link-class="button">
            <span>{{ $t("recordings.downloadsummary") }}</span>
          </b-dropdown-item-btn>
          <b-dropdown-item-btn @click="downloadRecordingZip(classroom.main_recording)" link-class="button">
            <span>{{ $t("recordings.downloadall") }}</span>
          </b-dropdown-item-btn>
        </b-dropdown>
      </div>
    </div>
    <ReportSidebar :classroom="classroom" icon-color="#ccc"/>
    <EventsReportSidebar v-if="hasRecordingReport" :recording="classroom.main_recording" icon-color="#ccc"/>
    <EditClassroomSidebar :classroom="classroom" :groups="user_groups"/>
  </div>
</template>

<script>
import DotsHorizontalIcon from 'vue-material-design-icons/DotsHorizontal.vue'
import LoadingIcon from 'vue-material-design-icons/Loading.vue'
import PhoneAlertOutline from 'vue-material-design-icons/PhoneAlertOutline.vue'
import jwtMixin from '../../../mixins/jwtMixin'
import utilitiesMixin from '../../../mixins/utilitiesMixin'
import recordingPlaybackMixin from '../../../mixins/recordingPlaybackMixin'
import ReportSidebar from '../SessionsModals/ReportSidebar'
import EventsReportSidebar from '../SessionsModals/EventsReportSidebar'
import EditClassroomSidebar from '../SessionsModals/EditClassroomSidebar'

export default {
  name: 'SessionCard',
  components: { DotsHorizontalIcon, LoadingIcon, PhoneAlertOutline, ReportSidebar, EventsReportSidebar, EditClassroomSidebar },
  mixins: [jwtMixin, utilitiesMixin, recordingPlaybackMixin],
  props: {
    classroom: { type: Object, required: true },
    user_roles: { type: Array, default: () => [] },
    profiles: { type: Array, default: () => [] },
    user_groups: { type: Array, default: () => [] }
  },
  computed: {
    hostName () {
      const host = this.classroom && this.classroom.host
      return (host && host.fullName) || null
    },
    canBeFavorite () {
      // EdF: El backend sólo admite favoritos en COLLAB/COLLABSECONDARY/ZOOM
      // (updateEnrollmentFavorite -> errors.validation.favoriteonlyforcollabandzoom)
      return this.classroom.targetSystem !== 'CONNECT'
    },
    hasRecordingReport () {
      // EdF: El informe de eventos de grabación sólo existe para las grabaciones de
      // Collaborate, misma condición que en Recordings.vue
      const recording = this.classroom.main_recording
      return !!recording && recording.targetSystem === 'COLLABSECONDARY'
    },
    supportsAttendance () {
      // EdF: La cualificación de asistencia se calcula sobre una grabación persistida, que CONNECT no
      // tiene (ClassroomServiceImpl.enrol sólo la calcula para COLLAB/COLLABSECONDARY/ZOOM). En CONNECT
      // attendanceQualified llega null, que significa "no se puede determinar", no "no asistió".
      return this.classroom.targetSystem !== 'CONNECT'
    }
  },
  methods: {
    sessionState (classroom) {
      if (this.isOngoing(classroom)) return 'in-progress'
      if (this.notYetStarted(classroom)) return 'not-started'
      if (this.hasEnded(classroom)) return 'finished'
      return null
    },
    classDuration (classroom) {
      const minutes = window.moment(classroom.end).diff(window.moment(classroom.start), 'minutes')
      return String(minutes).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    toggleFavorite (classroom) {
      // EdF: La card se pinta antes de que responda el POST de matrícula, así que durante ese intervalo
      // no hay enrollment.id que enviar. Sin esta guarda un clic temprano en la estrella lanza un TypeError.
      if (!classroom.enrollment) return
      const newFavorite = !classroom.enrollment.favorite
      window.axios.put(`classrooms/${classroom.id}/enrollments/${classroom.enrollment.id}/favorite`, newFavorite, { headers: { 'Content-Type': 'application/json' } })
        .then(() => { classroom.enrollment.favorite = newFavorite })
    },
    toggleRecordingView (recording) {
      const userId = this.jwtLoggedUserId()
      const payload = { recordingId: recording.id, userId, viewed: recording.viewed }
      if (recording.viewed) payload.playing = false
      window.axios.patch('recordings/' + recording.id + '/user/' + userId, payload)
      if (recording.viewed) this.$emit('recording-viewed', recording.id)
    },
    downloadRecordingZip (recording) {
      if (!recording.canDownload) return
      window.axios.get('recordings/' + recording.id + '/download-zip', { responseType: 'blob' })
        .then((response) => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(response.data)
          link.download = recording.name + '.zip'
          link.click()
          URL.revokeObjectURL(link.href)
        })
    },
    downloadSummary (recording) {
      const blob = new Blob([recording.summary], { type: 'text/plain;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = recording.name + '.txt'
      link.click()
      URL.revokeObjectURL(link.href)
    },
    launchUrl (url, target) {
      window.open(url, target)
    },
    dispatchShowReportEvent (classroomId) {
      window.EventBus.$emit('show-report', classroomId)
    },
    dispatchShowRecordingReportEvent (recordingId) {
      window.EventBus.$emit('show-report', recordingId)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Lti/SessionsComponents/SessionCard.vue